import { userRoleGroups } from '@common/interfaces/user'
import { Middleware } from '@reduxjs/toolkit'
import { Action } from 'redux'
import { IApi } from '../api'
import { isInAtlasModuleFrame } from '../helpers/atlasModuleFrame'
import {
  clearSnapshots,
  pickChannelsSnapshot,
  readSnapshot,
  SNAPSHOT_VERSION,
  snapshotKey,
  writeSnapshot,
} from '../helpers/offlineSnapshot'
import { decodeTokenClaims, isTokenExpired } from '../helpers/tokenUtils'
import { HYDRATE_CHANNELS_SNAPSHOT } from './channels/actions'
import { loadProfile, PROFILE_HYDRATED, PROFILE_SUCCESS, SIGN_OUT_ACTIONS } from './profile/actions'
import { IRootState } from './reducer'

/** Coalesces a burst of socket events into one write. */
const SNAPSHOT_WRITE_DELAY_MS = 3_000

/** AIM users only: a client user's portal needs data the snapshot doesn't hold. */
const isSnapshotUser = (state: IRootState): boolean => {
  const role = state.profile?.user?.role
  return !!role && userRoleGroups.MESSAGING.includes(role)
}

const currentSnapshotKey = (api: IApi): string | null => {
  const token = api.client.token
  if (!token || isTokenExpired(token)) return null
  const claims = decodeTokenClaims(token)
  return claims?.id ? snapshotKey(claims.id, claims.clientId) : null
}

/**
 * Put the last-known profile and AIM sidebar/timelines in the store before first render, so the
 * app opens with content — and opens at all offline. The network profile still loads (MainLayout)
 * and replaces it; a revoked token still ends the session through the usual 401 path.
 */
export const restoreOfflineSnapshot = async (
  api: IApi,
  dispatch: (action: Action & { data?: unknown }) => unknown,
): Promise<void> => {
  const key = currentSnapshotKey(api)
  if (!key) return
  const snapshot = await readSnapshot(key)
  if (!snapshot || snapshot.user.id !== key.split(':')[0]) return
  dispatch({ type: PROFILE_HYDRATED, data: snapshot.user })
  dispatch({ type: HYDRATE_CHANNELS_SNAPSHOT, data: snapshot.channels })
}

/** Keeps the snapshot current, drops it on sign-out, and finishes a profile load deferred offline. */
const offlineSnapshotMiddleware =
  (api: IApi): Middleware<object, IRootState> =>
  (store) => {
    let writeTimer: ReturnType<typeof setTimeout> | null = null
    let hasLiveProfile = false

    const writeNow = () => {
      if (writeTimer) clearTimeout(writeTimer)
      writeTimer = null
      const state = store.getState()
      const key = currentSnapshotKey(api)
      const user = state.profile?.user
      if (!key || !user || !isSnapshotUser(state)) return
      void writeSnapshot(key, {
        version: SNAPSHOT_VERSION,
        savedAt: Date.now(),
        user,
        channels: pickChannelsSnapshot(state.channels),
      })
    }

    const scheduleWrite = () => {
      if (!writeTimer) writeTimer = setTimeout(writeNow, SNAPSHOT_WRITE_DELAY_MS)
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden' && writeTimer) writeNow()
      })
    }
    if (typeof window !== 'undefined') {
      // Opened offline from the snapshot: the profile load failed, and nothing retries it — so
      // no socket either. Coming back online is the cue.
      window.addEventListener('online', () => {
        if (!hasLiveProfile && store.getState().profile?.user) {
          void store.dispatch(loadProfile() as unknown as Action)
        }
      })
    }

    return (next) => (action: { type?: string }) => {
      const before = store.getState()
      const result = next(action)

      if (action.type && SIGN_OUT_ACTIONS.includes(action.type)) {
        hasLiveProfile = false
        if (writeTimer) clearTimeout(writeTimer)
        writeTimer = null
        void clearSnapshots()
        return result
      }
      if (action.type === PROFILE_SUCCESS) hasLiveProfile = true
      // The hydrated profile marks the app initialized, so MainLayout won't load the live one.
      if (action.type === PROFILE_HYDRATED && !hasLiveProfile) {
        void store.dispatch(loadProfile() as unknown as Action)
      }

      const after = store.getState()
      const hasChanged =
        after.channels !== before.channels || after.profile?.user !== before.profile?.user
      // A module-panel frame shares the tab's IndexedDB but never holds the newer channels state.
      if (hasChanged && isSnapshotUser(after) && !isInAtlasModuleFrame()) scheduleWrite()
      return result
    }
  }

export default offlineSnapshotMiddleware
